import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Divider, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

import { FormShowTemplate } from 'framework/form';

import { Alert, Loading } from 'framework/ui/mui-builder';
import { useProjectDataContext } from 'domain/shared/newProject';

import {
  StaffingAssignmentListMember,
  StaffingAssignmentSlot,
  StaffingDataType,
  StaffingProjectAssignment,
  StaffingProjectData,
} from 'domain/shared/staffing';
import { ShowPageLayout } from 'framework/ui/layout';
import AssignmentConfirmationBreadcrumbs from './components/AssignmentConfirmationBreadcrumbs';
import ConfirmAssignmentsSlotList from './components/ConfirmAssignmentsSlotGrid';
import { ASSIGNMENTS_STATUS } from 'domain/shared/assignment/constants';
import { mapTeamStructureWithAssignments } from 'domain/shared/assignment';
import { GET_STAFFING_PROJECT_CONFIRMATION_DATA } from 'domain/shared/assignment/graphql/assignmentsQueries';
import { SLOT_LIST_SOURCE } from './constants';

type QueryResult = {
  baseProject: StaffingDataType;
};

type ConfirmPageProjectData = StaffingProjectData & {
  legacyId?: number;
  team?: StaffingDataType['team'];
  assignments?: StaffingProjectAssignment[];
  name?: string;
};

const mapAssignmentToMember = (assignment: StaffingProjectAssignment): StaffingAssignmentListMember => {
  const currentDate = new Date();
  const isFuture = assignment.fromDate && new Date(assignment.fromDate) > currentDate;

  return {
    id: assignment.id,
    assignmentId: assignment.id,
    slotId: assignment.slotId ?? null,
    employeeAlias: assignment.employeeAlias,
    employeeId: assignment.employeeAlias,
    positionId: assignment.positionId ?? null,
    technologyId: assignment.technologyId ? String(assignment.technologyId) : null,
    competencyLevel: assignment.competencyLevel ?? null,
    fte: Number(assignment.maxPercent || 0) / 100,
    minPercent: Number(assignment.minPercent ?? 0),
    maxPercent: Number(assignment.maxPercent ?? 0),
    role: assignment.membershipRole ?? null,
    membershipRole: assignment.membershipRole,
    isProbation: assignment.isProbation || false,
    isBackup: assignment.isBackup || false,
    isSpof: assignment.isSpof ?? false,
    isBench: assignment.isBench || false,
    backup: assignment.isBackup || false,
    bench: assignment.isBench || false,
    startDate: assignment.fromDate || '',
    endDate: assignment.toDate || '',
    from: assignment.fromDate,
    to: assignment.toDate,
    noteText: assignment.noteText ?? null,
    issues: [],
    isVirtual: assignment.isVirtual || false,
    isPending: assignment.status === ASSIGNMENTS_STATUS.PENDING,
    status: assignment.status || (isFuture ? ASSIGNMENTS_STATUS.FUTURE : ASSIGNMENTS_STATUS.ACTIVE),
  };
};

const buildSlots = (projectData: ConfirmPageProjectData): StaffingAssignmentSlot[] => {
  const mappedSlots = mapTeamStructureWithAssignments(projectData, projectData.team as never).team.expectedStructure;
  const mappedSlotIds = new Set(mappedSlots.map(slot => String(slot.id)));
  const allAssignments = (projectData.assignments || []) as StaffingProjectAssignment[];

  const unassignedAssignments = allAssignments.filter(
    assignment => !assignment.slotId || !mappedSlotIds.has(String(assignment.slotId)),
  );

  if (!unassignedAssignments.length) {
    return mappedSlots;
  }

  const unassignedSlot: StaffingAssignmentSlot = {
    id: 'unassigned-slot',
    teamId: projectData.team?.id || '',
    positionId: '',
    technologyId: undefined,
    competencyLevel: undefined,
    requiredFte: 0,
    requiredRole: undefined,
    description: 'Without expected position',
    assignedFte: unassignedAssignments.reduce((sum, assignment) => sum + Number(assignment.maxPercent || 0) / 100, 0),
    issues: [],
    members: unassignedAssignments.map(mapAssignmentToMember),
  };

  return [...mappedSlots, unassignedSlot];
};

const ConfirmAssignmentsLayout = () => {
  const { id } = useParams<string>();
  const { data: projectFromContext } = useProjectDataContext();
  const [reloadKey, setReloadKey] = useState(0);

  const { data, loading, error, refetch } = useQuery<QueryResult>(GET_STAFFING_PROJECT_CONFIRMATION_DATA, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const projectData = (data?.baseProject || null) as ConfirmPageProjectData | null;
  const slots = projectData ? buildSlots(projectData) : [];
  const assignmentsById = Object.fromEntries(
    ((projectData?.assignments || []) as StaffingProjectAssignment[]).map(assignment => [
      String(assignment.id),
      assignment,
    ]),
  );

  if (!id) {
    return <Alert severity="error">`id` route param is required.</Alert>;
  }

  if (loading && !projectData) {
    return <Loading isInitialPosition />;
  }

  if (error) {
    return <Alert severity="error">Failed to load project assignments.</Alert>;
  }

  return (
    <ShowPageLayout
      entityHeader={
        <Typography variant="h1" color="brand">
          Assignments Confirmation
        </Typography>
      }
      breadcrumbs={<AssignmentConfirmationBreadcrumbs />}
    >
      <FormShowTemplate
        key={reloadKey}
        initialData={{
          [SLOT_LIST_SOURCE]: slots,
        }}
        submitFormFunc={() => Promise.resolve({})}
        afterSubmit={{}}
      >
        <Stack spacing={2}>
          {projectData && (
            <Typography variant="body1" color="textSecondary">
              Project: {projectData.name || projectFromContext?.name}
            </Typography>
          )}
          <Typography variant="body1" color="textSecondary">
            Please review the assignments below and confirm them to assign the respective individuals to the project.
            Confirmation is available only once.
          </Typography>

          <Divider />

          <ConfirmAssignmentsSlotList
            onReload={async () => {
              await refetch();
              setReloadKey(prev => prev + 1);
            }}
            projectStartDate={projectData?.budget?.softStartDate}
            assignmentsById={assignmentsById}
          />
          <Typography variant="body1" color="textSecondary">
            After confirming the virtual assignment, the person will be assigned to the project.
          </Typography>
        </Stack>
      </FormShowTemplate>
    </ShowPageLayout>
  );
};

export default ConfirmAssignmentsLayout;
